import { useId, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Icon } from "../icons";
import type { IconName } from "../icons";
import { rovingIndex } from "./roving";

export type TabItem<T extends string> = {
  id: T;
  label: string;
  icon?: IconName | null;
  badge?: number | string | null;
  disabled?: boolean;
};

export type TabsProps<T extends string> = {
  tabs: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  label: string;
  className?: string;
  fill?: boolean;
  dataFxmap?: string;
  children?: (active: T, panelProps: { id: string; role: "tabpanel"; "aria-labelledby": string }) => ReactNode;
};

export function Tabs<T extends string>({ tabs, value, onChange, label, className, fill = false, dataFxmap, children }: TabsProps<T>) {
  const baseId = useId();
  const refs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});
  const tabId = (id: T) => `${baseId}-tab-${id}`;
  const panelId = (id: T) => `${baseId}-panel-${id}`;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const enabled = tabs.filter(a => !a.disabled);
    const index = enabled.findIndex(a => a.id === value);
    const next = rovingIndex(event.key, index, enabled.length, "horizontal");

    if (next < 0) return;

    event.preventDefault();

    const target = enabled[next];

    if (target === undefined) return;

    onChange(target.id);
    refs.current[target.id]?.focus();
  };

  return (
    <div className={["fxmap-tabs", className ?? ""].filter(Boolean).join(" ")} data-fxmap={dataFxmap}>
      <div className={["fxmap-tablist", fill ? "fxmap-tablist-fill" : ""].filter(Boolean).join(" ")} role="tablist" aria-label={label} onKeyDown={onKeyDown}>
        {tabs.map(tab => {
          const selected = tab.id === value;

          return (
            <button
              key={tab.id}
              ref={element => {
                refs.current[tab.id] = element;
              }}
              id={tabId(tab.id)}
              type="button"
              role="tab"
              className={["fxmap-tab", selected ? "fxmap-tab-on" : ""].filter(Boolean).join(" ")}
              aria-selected={selected}
              aria-controls={panelId(tab.id)}
              tabIndex={selected ? 0 : -1}
              disabled={tab.disabled}
              data-fxmap-action={`tab-${tab.id}`}
              onClick={() => onChange(tab.id)}
            >
              {tab.icon ? <Icon name={tab.icon} /> : null}
              <span className="fxmap-tab-label">{tab.label}</span>
              {tab.badge !== null && tab.badge !== undefined ? <span className="fxmap-tab-badge fxmap-tabular">{tab.badge}</span> : null}
            </button>
          );
        })}
      </div>
      {children ? children(value, { id: panelId(value), role: "tabpanel", "aria-labelledby": tabId(value) }) : null}
    </div>
  );
}
